// v3.15-1210 ADMIN-006 — Recognition & Leaderboards (read-only projection).
//
// Aggregates existing immutable records + replayable projections into
// leaderboards & recognition feeds. Never writes anywhere. Never
// evaluates badges. Never calculates compensation.
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Trophy, Medal, Star, ShieldCheck, TrendingUp, Users, Building2, Award } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';

const METRICS = [
  { key: 'personal_monthly', label: 'Personal Production (Month)' },
  { key: 'team_monthly',     label: 'Team Production (Month)' },
  { key: 'company_monthly',  label: 'Company Production (Month)' },
  { key: 'ytd',              label: 'Year-to-Date Production' },
  { key: 'lifetime',         label: 'Lifetime Production' },
];

const fmtMoney = (n) => n == null ? '—' : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmtDate = (iso) => (iso ? iso.slice(0, 10) : '—');

const iconFor = (tok, cls = 'w-4 h-4') => {
  switch (tok) {
    case 'trophy': return <Trophy className={cls} />;
    case 'star':   return <Star className={cls} />;
    case 'shield': return <ShieldCheck className={cls} />;
    default:       return <Medal className={cls} />;
  }
};

const rankIcon = (rank) => {
  if (rank === 1) return <Trophy className="w-4 h-4 text-amber-500" />;
  if (rank === 2) return <Medal  className="w-4 h-4 text-slate-400" />;
  if (rank === 3) return <Medal  className="w-4 h-4 text-amber-800" />;
  return null;
};

/* ============================== Page ============================== */

export default function RecognitionPage() {
  const [catalog, setCatalog] = useState({ companies: [], plans: [], roles: [], metrics: METRICS.map(m => m.key) });
  const [error, setError]     = useState(null);

  // Filters
  const [metric, setMetric]           = useState('personal_monthly');
  const [companyId, setCompanyId]     = useState('');
  const [role, setRole]               = useState('');
  const [planId, setPlanId]           = useState('');
  const [month, setMonth]             = useState('');
  const [year, setYear]               = useState('');

  // Data
  const [prodBoard, setProdBoard]     = useState(null);
  const [tierBoard, setTierBoard]     = useState(null);
  const [badgeBoard, setBadgeBoard]   = useState(null);
  const [recent, setRecent]           = useState([]);
  const [loading, setLoading]         = useState(true);

  const [tab, setTab] = useState('production');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/recognition/filter-catalog');
        setCatalog({
          metrics: data.metrics,
          companies: data.companies || [],
          plans: data.plans || [],
          roles: data.roles || [],
        });
      } catch (e) {
        if (e?.response?.status === 403) setError('Recognition surface is restricted to admins.');
      }
    })();
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        company_id: companyId || undefined,
        role:       role      || undefined,
        compensation_plan_id: planId || undefined,
        month:      month     || undefined,
        year:       year      || undefined,
      };
      const [prod, tier, badge, rec] = await Promise.all([
        api.get('/recognition/leaderboards/production', { params: { ...params, metric } }),
        api.get('/recognition/leaderboards/tier',        { params }),
        api.get('/recognition/leaderboards/badge',       { params: { company_id: companyId || undefined, role: role || undefined } }),
        api.get('/recognition/recent-badges',            { params: { company_id: companyId || undefined } }),
      ]);
      setProdBoard(prod.data);
      setTierBoard(tier.data);
      setBadgeBoard(badge.data);
      setRecent(rec.data?.items || []);
    } catch (e) {
      if (e?.response?.status === 403) setError('Recognition surface is restricted to admins.');
      else toast.error(e?.response?.data?.detail || 'Failed to load recognition data');
    } finally { setLoading(false); }
  }, [metric, companyId, role, planId, month, year]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Filter plans by selected company for the plan dropdown.
  const scopedPlans = companyId
    ? catalog.plans.filter((p) => p.company_id === companyId)
    : catalog.plans;

  return (
    <div className="space-y-4" data-testid="recognition-page">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Trophy className="w-6 h-6 text-amber-600" />
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">Recognition &amp; Leaderboards</h1>
            <p className="text-sm text-slate-500">
              Read-only projection layer. All figures derive from immutable Compensation Events / Deals
              and the replayable Employee Badge projection.
            </p>
          </div>
        </div>
      </div>

      {error ? (
        <div className="p-6 bg-rose-50 text-rose-700 border border-rose-200 rounded-xl" data-testid="rc-error">
          {error}
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm" data-testid="rc-filters">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Metric</label>
                <select data-testid="rc-metric" value={metric} onChange={(e) => setMetric(e.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                  {METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Company</label>
                <select data-testid="rc-company" value={companyId} onChange={(e) => { setCompanyId(e.target.value); setPlanId(''); }}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                  <option value="">All companies</option>
                  {catalog.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Role</label>
                <select data-testid="rc-role" value={role} onChange={(e) => setRole(e.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                  <option value="">All roles</option>
                  {catalog.roles.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Compensation Plan</label>
                <select data-testid="rc-plan" value={planId} onChange={(e) => setPlanId(e.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                  <option value="">All plans</option>
                  {scopedPlans.map((p) => (
                    <option key={p.id} value={p.id}>{p.plan_name || p.plan_code} · {p.role} L{p.level || 1}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Month (YYYY-MM)</label>
                <input data-testid="rc-month" value={month} onChange={(e) => setMonth(e.target.value)}
                       placeholder="2026-07"
                       className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Year (YTD)</label>
                <input data-testid="rc-year" value={year} onChange={(e) => setYear(e.target.value)}
                       placeholder="2026"
                       className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 border-b border-slate-200">
            {['production', 'tiers', 'badges', 'recent'].map((t) => (
              <button key={t} data-testid={`rc-tab-${t}`} onClick={() => setTab(t)}
                      className={
                        'px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ' +
                        (tab === t ? 'border-amber-600 text-amber-700' : 'border-transparent text-slate-500 hover:text-slate-800')
                      }>
                {t === 'production' ? 'Production' : t === 'tiers' ? 'Compensation Tiers' : t === 'badges' ? 'Badges' : 'Recent Awards'}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="p-10 flex items-center justify-center text-slate-500 bg-white rounded-2xl border">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <>
              {/* Production tab */}
              {tab === 'production' && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto" data-testid="rc-production-board">
                  {(prodBoard?.items || []).length === 0 ? (
                    <div className="p-10 text-center text-slate-500" data-testid="rc-production-empty">
                      No data for the current filters.
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                        <tr>
                          <th className="text-left px-3 py-2 w-16">Rank</th>
                          <th className="text-left px-3 py-2">
                            {metric === 'company_monthly' ? 'Company' : 'Employee'}
                          </th>
                          <th className="text-left px-3 py-2">Role</th>
                          <th className="text-left px-3 py-2">Company</th>
                          <th className="text-right px-3 py-2">Production</th>
                        </tr>
                      </thead>
                      <tbody>
                        {prodBoard.items.map((r, i) => (
                          <tr key={r.employee_id || r.company_id} data-testid={`rc-row-${i}`}
                              className="border-t border-slate-100">
                            <td className="px-3 py-2">
                              <span className="inline-flex items-center gap-1.5">
                                {rankIcon(r.rank || i + 1)}
                                <span className="font-bold text-slate-700">#{r.rank || i + 1}</span>
                              </span>
                            </td>
                            <td className="px-3 py-2 font-semibold text-slate-800">
                              {r.employee_name || r.company_name || '—'}
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-500">{r.role || '—'}</td>
                            <td className="px-3 py-2 text-xs text-slate-500">{r.company_name || '—'}</td>
                            <td className="px-3 py-2 text-right font-extrabold text-emerald-700">
                              {fmtMoney(r.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Tiers tab */}
              {tab === 'tiers' && (
                <div className="space-y-4" data-testid="rc-tier-board">
                  {(tierBoard?.items || []).length === 0 ? (
                    <div className="p-10 text-center text-slate-500 bg-white border rounded-2xl">No tier data.</div>
                  ) : (
                    tierBoard.items.map((b, i) => (
                      <div key={i} data-testid={`rc-tier-${i}`} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <div className="text-xs uppercase tracking-wider text-slate-500">Tier</div>
                            <div className="text-lg font-extrabold text-blue-700">
                              {b.rate != null ? `${b.rate}%` : 'Unranked'}
                              {b.badge ? <span className="ml-2 text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">{b.badge}</span> : null}
                            </div>
                            <div className="text-[11px] text-slate-500">
                              {b.min != null ? `${fmtMoney(b.min)} – ${b.max ? fmtMoney(b.max) : '∞'}` : '—'}
                            </div>
                          </div>
                          <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                            {b.count} employee{b.count === 1 ? '' : 's'}
                          </span>
                        </div>
                        <table className="w-full text-sm mt-2">
                          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                            <tr>
                              <th className="text-left px-2 py-1">Employee</th>
                              <th className="text-left px-2 py-1">Role</th>
                              <th className="text-left px-2 py-1">Company</th>
                              <th className="text-right px-2 py-1">Production</th>
                            </tr>
                          </thead>
                          <tbody>
                            {b.employees.map((e) => (
                              <tr key={e.employee_id} className="border-t border-slate-100">
                                <td className="px-2 py-1 font-semibold">{e.employee_name}</td>
                                <td className="px-2 py-1 text-xs text-slate-500">{e.role}</td>
                                <td className="px-2 py-1 text-xs text-slate-500">{e.company_name || '—'}</td>
                                <td className="px-2 py-1 text-right font-semibold text-emerald-700">{fmtMoney(e.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Badges tab */}
              {tab === 'badges' && (
                <div className="space-y-4" data-testid="rc-badge-board">
                  {(badgeBoard?.items || []).length === 0 ? (
                    <div className="p-10 text-center text-slate-500 bg-white border rounded-2xl">No badges awarded yet.</div>
                  ) : (
                    badgeBoard.items.map((b) => (
                      <div key={b.badge_id} data-testid={`rc-badge-${b.badge_id}`} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className="w-9 h-9 rounded-lg bg-amber-50 border border-amber-200 text-amber-600 flex items-center justify-center">
                              {iconFor(b.badge_icon)}
                            </span>
                            <div>
                              <div className="text-lg font-extrabold text-slate-900">
                                {b.badge_order}. {b.badge_name}
                              </div>
                              <div className="text-[11px] text-slate-500">Highest-order current badge</div>
                            </div>
                          </div>
                          <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                            {b.count} employee{b.count === 1 ? '' : 's'}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {b.employees.map((e) => (
                            <span key={e.employee_id} data-testid={`rc-badge-emp-${e.employee_id}`}
                                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-slate-50 border border-slate-200">
                              <span className="font-semibold text-slate-800">{e.employee_name}</span>
                              <span className="text-slate-400">·</span>
                              <span className="text-slate-500">{e.role}</span>
                              <span className="text-slate-400">·</span>
                              <span className="text-slate-500">{fmtDate(e.awarded_at)}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Recent awards */}
              {tab === 'recent' && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto" data-testid="rc-recent-feed">
                  {recent.length === 0 ? (
                    <div className="p-10 text-center text-slate-500" data-testid="rc-recent-empty">No recent badge awards.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                        <tr>
                          <th className="text-left px-3 py-2">Awarded</th>
                          <th className="text-left px-3 py-2">Employee</th>
                          <th className="text-left px-3 py-2">Badge</th>
                          <th className="text-left px-3 py-2">Program</th>
                          <th className="text-left px-3 py-2">Approvers</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recent.map((e) => (
                          <tr key={e.id} data-testid={`rc-recent-${e.id}`} className="border-t border-slate-100">
                            <td className="px-3 py-2 whitespace-nowrap">{fmtDate(e.awarded_at)}</td>
                            <td className="px-3 py-2 font-semibold">{e.employee_name || e.employee_id}</td>
                            <td className="px-3 py-2">
                              <span className="inline-flex items-center gap-1 font-semibold text-amber-700">
                                {iconFor(e.badge_icon, 'w-3.5 h-3.5')}
                                {e.badge_order}. {e.badge_name}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-500">{e.badge_program_name}</td>
                            <td className="px-3 py-2 text-xs text-slate-500">
                              {e.manager_approver_email && <div>Mgr: {e.manager_approver_email}</div>}
                              {e.executive_approver_email && <div>Exec: {e.executive_approver_email}</div>}
                              {!e.manager_approver_email && !e.executive_approver_email && '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
